// [PURPOSE]: Login/Signup modal for Firebase authentication
// [WHY]: Users need to create accounts for premium features, cloud sync, AI tokens
// [INTEGRATION]: Triggered by PremiumGate, AccountPanel, or header login button
// [CREATED]: v1.0 Track 2A — Firebase Auth UI

import React, { useState } from 'react';
import { Mail, Lock, User, LogIn, UserPlus, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase/config';
import MobileModal from './MobileModal';

/**
 * [PURPOSE]: Authentication modal with login/signup tabs
 * [UX]: Clean, dark-themed modal matching app design
 * [FEATURES]: Email/password, Google Sign-In, error display, loading states
 *
 * @param {boolean} isOpen - Whether modal is visible
 * @param {Function} onClose - Callback to close modal
 * @param {string} initialMode - 'login' or 'signup' (default: 'login')
 * @param {string} prompt - Optional message explaining why login is needed
 */
const AuthModal = ({ isOpen, onClose, initialMode = 'login', prompt = null }) => {
  const { login, signup, loginWithGoogle, isAuthenticated } = useAuth();

  // [UI-MOBILEMODAL-ADOPTION wave 1, 2026-07-04]: Escape dismissal now comes
  // from MobileModal (it registers useModalEscape itself) — an own call here
  // would double-register the document keydown listener.
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  // [S5]: ToS acceptance checkbox state
  const [tosAccepted, setTosAccepted] = useState(false);

  // [GUARD]: Don't show if already authenticated or not open
  if (!isOpen || isAuthenticated) return null;

  // ========================================
  // HANDLERS
  // ========================================

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (mode === 'signup') {
      // Validate passwords match
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }
      if (password.length < 6) {
        setError('Password must be at least 6 characters');
        setLoading(false);
        return;
      }

      const result = await signup(email, password, displayName, tosAccepted);
      if (result.success) {
        onClose();
      } else {
        setError(result.error);
      }
    } else {
      const result = await login(email, password);
      if (result.success) {
        onClose();
      } else {
        setError(result.error);
      }
    }

    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);

    const result = await loginWithGoogle();
    if (result.success) {
      onClose();
    } else if (result.error) {
      setError(result.error);
    }

    setLoading(false);
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'signup' : 'login');
    setError('');
    setResetSent(false);
    setTosAccepted(false);
  };

  // [PURPOSE]: Send password reset email via Firebase Auth
  const handleResetPassword = async () => {
    if (!email) {
      setError('Enter your email address first');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch (err) {
      const msg = err.code === 'auth/user-not-found'
        ? 'No account found with that email'
        : err.code === 'auth/invalid-email'
        ? 'Invalid email address'
        : 'Failed to send reset email. Try again.';
      setError(msg);
    }
    setLoading(false);
  };

  // ========================================
  // RENDER
  // ========================================

  return (
    <MobileModal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'login' ? 'Sign In' : 'Create Account'}
      showSaveButton={false}
      overlayClassName="z-[70]"
    >
      {prompt && (
        <p className="text-sm text-blue-200 mb-3">{prompt}</p>
      )}

      {/* Body */}
      <div className="p-5">
          {/* Error Display */}
          {error && (
            <div className="mb-4 p-3 bg-red-900/30 border border-red-600/50 rounded-lg flex items-start gap-2">
              <AlertCircle size={16} className="text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          {/* Google Sign-In */}
          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full mb-4 p-3 bg-white hover:bg-gray-100 text-gray-800 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-gray-600"></div>
            <span className="text-xs text-gray-400 uppercase">or</span>
            <div className="flex-1 h-px bg-gray-600"></div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            {mode === 'signup' && (
              <div>
                <label className="block text-xs text-gray-400 mb-1">Display Name</label>
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Your character name"
                    className="w-full pl-10 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs text-gray-400 mb-1">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@example.com"
                  required
                  className="w-full pl-10 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-400 mb-1">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 6 characters"
                  required
                  minLength={6}
                  className="w-full pl-10 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {mode === 'login' && (
              <div className="flex justify-end -mt-1">
                {resetSent ? (
                  <span className="text-xs text-green-400">Reset email sent! Check your inbox.</span>
                ) : (
                  <button
                    type="button"
                    onClick={handleResetPassword}
                    disabled={loading}
                    className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
            )}

            {mode === 'signup' && (
              <div>
                <label className="block text-xs text-gray-400 mb-1">Confirm Password</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat password"
                    required
                    minLength={6}
                    className="w-full pl-10 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {/* [S5]: Terms of Service acceptance checkbox (signup only) */}
            {mode === 'signup' && (
              <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-gray-700/30 transition-colors">
                <input
                  type="checkbox"
                  checked={tosAccepted}
                  onChange={(e) => setTosAccepted(e.target.checked)}
                  className="mt-0.5 accent-blue-500"
                />
                <span className="text-xs text-gray-300 leading-relaxed">
                  I agree to the{' '}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms of Service
                  </a>
                  {' '}and{' '}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Privacy Policy
                  </a>
                </span>
              </label>
            )}

            <button
              type="submit"
              disabled={loading || (mode === 'signup' && !tosAccepted)}
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : mode === 'login' ? (
                <><LogIn size={16} /> Sign In</>
              ) : (
                <><UserPlus size={16} /> Create Account</>
              )}
            </button>
          </form>

          {/* Switch mode */}
          <p className="mt-4 text-center text-sm text-gray-400">
            {mode === 'login' ? (
              <>Don't have an account?{' '}
                <button onClick={switchMode} className="text-blue-400 hover:text-blue-300 font-medium">
                  Sign up
                </button>
              </>
            ) : (
              <>Already have an account?{' '}
                <button onClick={switchMode} className="text-blue-400 hover:text-blue-300 font-medium">
                  Sign in
                </button>
              </>
            )}
          </p>
        </div>

      {/* Footer */}
      <div className="px-5 py-3 bg-gray-900/50 border-t border-gray-700">
        <p className="text-xs text-gray-500 text-center">
          Free tier: 1 character. Unlock unlimited characters, AI art, and more for $3.99.
        </p>
      </div>
    </MobileModal>
  );
};

export default AuthModal;
